import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import { OAR_TYPES, isRowingShell } from '../constants/boatClasses';
import { getLocalISODate } from '../utils/reservationTimes';
import { getSeatsForBoatClass } from '../utils/boatSeats';
import SquadManager from './SquadManager';
import AthleteSearchInput from './AthleteSearchInput';
import { GoalPercentPicker } from './KorzoTargetPanel';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function WorkoutBuilder() {
  const { user, isAdmin } = useAuth();
  const getDefaultReservationWindow = (session) => {
    const windows = {
      1: { start: '06:00', end: '08:00' },
      2: { start: '08:00', end: '10:00' },
      3: { start: '10:00', end: '12:00' },
      4: { start: '12:00', end: '14:00' },
      5: { start: '14:00', end: '16:00' }
    };
    return windows[session] || windows[1];
  };
  const toMinutes = (time) => {
    const [hours, minutes] = String(time || '00:00').split(':').map(v => parseInt(v, 10) || 0);
    return (hours * 60) + minutes;
  };
  const [workoutTitle, setWorkoutTitle] = useState('');
  const [workoutNotes, setWorkoutNotes] = useState('');
  const [workoutGoalPercent, setWorkoutGoalPercent] = useState('');
  const [targetTimesEnabled, setTargetTimesEnabled] = useState(false);
  const [sessionNumber, setSessionNumber] = useState(1);
  const defaultWindow = getDefaultReservationWindow(1);
  const [reservationStartTime, setReservationStartTime] = useState(defaultWindow.start);
  const [reservationEndTime, setReservationEndTime] = useState(defaultWindow.end);
  const [workoutTeam, setWorkoutTeam] = useState('');
  const [squads, setSquads] = useState([]);
  const [squadMembers, setSquadMembers] = useState([]);
  const [workoutSquadId, setWorkoutSquadId] = useState('');
  const [showSquadManager, setShowSquadManager] = useState(false);
  const [boatings, setBoatings] = useState([]);
  const [workoutDate, setWorkoutDate] = useState(null);
  const [athleteRoster, setAthleteRoster] = useState([]);
  const [boatRoster, setBoatRoster] = useState([]);
  const [oarRoster, setOarRoster] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [existingSessions, setExistingSessions] = useState([]);
  const [planSessions, setPlanSessions] = useState([]);
  const [loadStatus, setLoadStatus] = useState('');
  const [loadStatusTone, setLoadStatusTone] = useState('info');
  const flashStatus = (message, tone = 'info') => {
    setLoadStatus(message);
    setLoadStatusTone(tone);
  };
  const [availableTeams, setAvailableTeams] = useState([]);
  const [showOarAssignModal, setShowOarAssignModal] = useState(false);
  const [modalOarAssignments, setModalOarAssignments] = useState({});
  const [showOarSplitManageModal, setShowOarSplitManageModal] = useState(false);
  const [oarSplitActionLoadingId, setOarSplitActionLoadingId] = useState(null);
  
  // Sign-up settings
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [signupLimit, setSignupLimit] = useState('');
  /** When saving, attach lineup names (resolved to users) to auto-created boat reservations */
  const [attachCrewToReservations, setAttachCrewToReservations] = useState(true);
  const [savedLineups, setSavedLineups] = useState([]);
  const [lineupApplySelect, setLineupApplySelect] = useState({});
  const [lineupUpdateSelect, setLineupUpdateSelect] = useState({});
  const [savingLineupBoatIndex, setSavingLineupBoatIndex] = useState(null);
  const [saveLineupName, setSaveLineupName] = useState('');

  const addBoat = () => {
    setBoatings([...boatings, {
      boatName: '',
      boatClass: '',
      seats: [], // Array of { seat: 'Stroke', athlete: '' }
      showAvgWeight: false,
      showAvgErg: false,
      oarId: null,
      oarName: ''
    }]);
  };

  const updateBoatOption = (index, option, value) => {
    const updated = [...boatings];
    updated[index][option] = value;
    setBoatings(updated);
  };

  const removeBoat = (index) => {
    const updated = [...boatings];
    updated.splice(index, 1);
    setBoatings(updated);
  };

  const updateBoatSelection = (index, boatName) => {
    const updated = [...boatings];
    updated[index].boatName = boatName;

    // Find the boat class from the roster
    const selectedBoat = boatRoster.find(b => b.name === boatName);
    const boatClass = selectedBoat?.boat_class || '';
    updated[index].boatClass = boatClass;

    // Generate seats based on boat class
    const { seats } = getSeatsForBoatClass(boatClass);
    updated[index].seats = seats.map(seat => ({ seat, athlete: '' }));

    // Reset oar selection when changing boats
    updated[index].oarId = null;
    updated[index].oarName = '';

    setBoatings(updated);
  };

  const updateSeatAthlete = (boatIndex, seatIndex, athleteName) => {
    const updated = [...boatings];
    updated[boatIndex].seats[seatIndex].athlete = athleteName;
    setBoatings(updated);
  };

  const updateOarSelection = (boatIndex, oarId, oarName) => {
    const updated = [...boatings];
    updated[boatIndex].oarId = oarId || null;
    updated[boatIndex].oarName = oarName || '';
    setBoatings(updated);
  };

  const getAssignableBoats = () => {
    return boatings
      .map((boat, index) => ({ boat, index }))
      .filter(({ boat }) => boat.boatName && isRowingShell(boat.boatClass));
  };

  const openOarAssignModal = () => {
    const initialAssignments = {};
    getAssignableBoats().forEach(({ boat, index }) => {
      initialAssignments[index] = boat.oarId ? String(boat.oarId) : '';
    });
    setModalOarAssignments(initialAssignments);
    setShowOarAssignModal(true);
  };

  const applyModalOarAssignments = () => {
    const selectedIds = Object.values(modalOarAssignments).filter(Boolean);
    const uniqueCount = new Set(selectedIds).size;
    if (uniqueCount !== selectedIds.length) {
      alert('Each oar set can only be assigned once per session.');
      return;
    }

    const updated = [...boatings];
    getAssignableBoats().forEach(({ index }) => {
      const selectedId = modalOarAssignments[index];
      const parsedId = selectedId ? parseInt(selectedId, 10) : null;
      const selectedOar = parsedId ? oarRoster.find(o => o.id === parsedId) : null;
      updated[index].oarId = parsedId;
      updated[index].oarName = selectedOar?.name || '';
    });

    setBoatings(updated);
    setShowOarAssignModal(false);
  };

  const clearAssignmentsForOarIds = (oarIds = []) => {
    if (!oarIds.length) return;
    const idSet = new Set(oarIds.map((id) => parseInt(id, 10)).filter(Boolean));
    if (idSet.size === 0) return;

    setBoatings((prev) =>
      prev.map((boat) => {
        if (boat.oarId && idSet.has(boat.oarId)) {
          return { ...boat, oarId: null, oarName: '' };
        }
        return boat;
      })
    );
  };

  const handleSplitOarSet = async (oar) => {
    const splitCountInput = window.prompt(
      `How many split sets should be created from "${oar.name}"?`,
      '2'
    );
    if (splitCountInput === null) return;
    const splitCount = parseInt(splitCountInput, 10);
    if (isNaN(splitCount) || splitCount < 2) {
      alert('Split count must be 2 or greater.');
      return;
    }

    const confirmed = window.confirm(
      `Split "${oar.name}" into ${splitCount} virtual sets?`
    );
    if (!confirmed) return;

    try {
      setOarSplitActionLoadingId(oar.id);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/oars/${oar.id}/split`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ splitCount })
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Failed to split oar set');
        return;
      }

      // Parent should no longer be assigned once split children exist.
      clearAssignmentsForOarIds([oar.id]);
      await fetchOars();
    } catch (error) {
      console.error('Error splitting oar set:', error);
      alert('Failed to split oar set');
    } finally {
      setOarSplitActionLoadingId(null);
    }
  };

  const handleRecombineOarSet = async (oar) => {
    const confirmed = window.confirm(
      `Recombine active split sets back into "${oar.name}"?\n\nThis retires the active split children.`
    );
    if (!confirmed) return;

    try {
      setOarSplitActionLoadingId(oar.id);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/oars/${oar.id}/recombine`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Failed to recombine oar set');
        return;
      }

      clearAssignmentsForOarIds(data.recombinedChildIds || []);
      await fetchOars();
    } catch (error) {
      console.error('Error recombining oar set:', error);
      alert('Failed to recombine oar set');
    } finally {
      setOarSplitActionLoadingId(null);
    }
  };

  // Legacy support: convert old format to new format
  const migrateBoatingData = (boat) => {
    // If boat already has seats array, return as is
    if (boat.seats && Array.isArray(boat.seats)) {
      return boat;
    }
    
    // Convert old format (athletes array + coxswains) to new format (seats array)
    const boatClass = boat.boatClass || '';
    const { seats: seatLabels, hasCox } = getSeatsForBoatClass(boatClass);
    const athletes = boat.athletes || [];
    const coxswains = boat.coxswains || [];
    
    const seats = seatLabels.map((seatLabel, idx) => {
      // If this is a cox seat, find the coxswain
      if (seatLabel === 'Cox') {
        const coxIdx = coxswains[0];
        return { seat: 'Cox', athlete: athletes[coxIdx] || '' };
      }
      
      // Find non-coxswain athletes
      const nonCoxAthletes = athletes.filter((_, i) => !coxswains.includes(i));
      return { seat: seatLabel, athlete: nonCoxAthletes[idx] || '' };
    });
    
    return {
      ...boat,
      seats,
    };
  };

  useEffect(() => {
    fetchAthletes();
    fetchBoats();
    fetchOars();
    fetchAttendance();
    fetchTeams();
  }, []);

  const fetchSquadsForTeam = async (team) => {
    if (!team) {
      setSquads([]);
      setSquadMembers([]);
      setWorkoutSquadId('');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/squads?team=${encodeURIComponent(team)}`,
        { headers: getAuthHeaders(token) }
      );
      if (!response.ok) {
        setSquads([]);
        setSquadMembers([]);
        return;
      }
      const data = await response.json();
      const squadList = data.squads || [];
      setSquads(squadList);
      setSquadMembers(data.members || []);
      setWorkoutSquadId((prev) =>
        prev && squadList.some((s) => String(s.id) === prev) ? prev : ''
      );
    } catch (error) {
      console.error('Error fetching squads:', error);
      setSquads([]);
      setSquadMembers([]);
    }
  };

  const fetchLineups = async (targetTeam) => {
    if (!targetTeam) {
      setSavedLineups([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/lineups?team=${encodeURIComponent(targetTeam)}`,
        { headers: getAuthHeaders(token) }
      );
      if (response.ok) {
        const data = await response.json();
        setSavedLineups(data.lineups || []);
      } else {
        setSavedLineups([]);
      }
    } catch (error) {
      console.error('Error fetching lineups:', error);
      setSavedLineups([]);
    }
  };

  // Load squads whenever the workout team changes.
  useEffect(() => {
    fetchSquadsForTeam(workoutTeam);
  }, [workoutTeam]);

  useEffect(() => {
    if (!workoutTeam) {
      setTargetTimesEnabled(false);
      return;
    }
    const token = localStorage.getItem('token');
    const query = isAdmin() ? `?team=${encodeURIComponent(workoutTeam)}` : '';
    fetch(`${API_URL}/api/teams/settings${query}`, { headers: getAuthHeaders(token) })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setTargetTimesEnabled(Boolean(data?.settings?.targetTimesEnabled)))
      .catch(() => setTargetTimesEnabled(false));
    // isAdmin is a new function each render; role is the actual input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutTeam, user?.role]);

  useEffect(() => {
    if (workoutTeam) {
      fetchAthletes(workoutTeam);
      fetchLineups(workoutTeam);
    }
  }, [workoutTeam]);

  const athletesSeatedElsewhere = (exceptBoatIndex) => {
    const names = new Set();
    boatings.forEach((boat, index) => {
      if (index === exceptBoatIndex) return;
      (boat.seats || []).forEach((seat) => {
        if (seat?.athlete) names.add(seat.athlete);
      });
    });
    return names;
  };

  const applySavedLineup = (boatIndex, lineupId) => {
    const lineup = savedLineups.find((item) => String(item.id) === String(lineupId));
    const boat = boatings[boatIndex];
    if (!lineup || !boat) return;
    if (boat.boatClass !== lineup.boatClass) {
      alert(`"${lineup.name}" is a ${lineup.boatClass} lineup. Select a ${lineup.boatClass} to use it.`);
      return;
    }
    const usedElsewhere = athletesSeatedElsewhere(boatIndex);
    const conflicts = (lineup.seats || [])
      .map((seat) => seat.athlete)
      .filter((name) => name && usedElsewhere.has(name));
    if (conflicts.length > 0) {
      alert(`Can't apply ${lineup.name}: already seated in another boat — ${conflicts.join(', ')}`);
      return;
    }
    const { seats: labels } = getSeatsForBoatClass(boat.boatClass);
    const bySeat = Object.fromEntries((lineup.seats || []).map((seat) => [seat.seat, seat.athlete || '']));
    const updated = [...boatings];
    updated[boatIndex] = {
      ...updated[boatIndex],
      seats: labels.map((seat) => ({ seat, athlete: bySeat[seat] || '' }))
    };
    setBoatings(updated);
    setLineupApplySelect((prev) => ({ ...prev, [boatIndex]: '' }));
    flashStatus(`Applied ${lineup.name} to ${boat.boatName || 'this boat'}.`, 'success');
  };

  const seatsForSave = (boat) => (boat.seats || []).map((seat) => ({
    seat: seat.seat,
    athlete: seat.athlete || ''
  }));

  const saveBoatAsLineup = async (boatIndex) => {
    const boat = boatings[boatIndex];
    const name = saveLineupName.trim();
    if (!isRowingShell(boat?.boatClass)) {
      alert('Select a shell before saving a lineup.');
      return;
    }
    if (!name) {
      alert('Give this lineup a name (e.g. 1v).');
      return;
    }
    if (!workoutTeam) {
      alert('Select a team first.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/lineups`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          team: workoutTeam,
          name,
          boatClass: boat.boatClass,
          seats: seatsForSave(boat)
        })
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Failed to save lineup');
        return;
      }
      await fetchLineups(workoutTeam);
      setSavingLineupBoatIndex(null);
      setSaveLineupName('');
      flashStatus(`Saved lineup ${name} (${boat.boatClass}).`, 'success');
    } catch (error) {
      console.error('Error saving lineup:', error);
      alert('Error saving lineup');
    }
  };

  const updateExistingLineupFromBoat = async (boatIndex, lineupId) => {
    const boat = boatings[boatIndex];
    const lineup = savedLineups.find((item) => String(item.id) === String(lineupId));
    if (!boat || !lineup) return;
    if (boat.boatClass !== lineup.boatClass) {
      alert(`"${lineup.name}" is a ${lineup.boatClass} lineup.`);
      return;
    }
    if (!confirm(`Replace ${lineup.name} (${lineup.boatClass}) with this boat's current seats?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/lineups/${lineup.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          name: lineup.name,
          boatClass: lineup.boatClass,
          seats: seatsForSave(boat)
        })
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Failed to update lineup');
        return;
      }
      await fetchLineups(workoutTeam);
      setLineupUpdateSelect((prev) => ({ ...prev, [boatIndex]: '' }));
      flashStatus(`Updated lineup ${lineup.name}.`, 'success');
    } catch (error) {
      console.error('Error updating lineup:', error);
      alert('Error updating lineup');
    }
  };

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get unique teams from athletes
      const response = await fetch(`${API_URL}/api/athletes`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        // Extract unique team names from athletes
        let teams;
        if (isAdmin()) {
          teams = [...new Set(
            data.flatMap((a) => (Array.isArray(a.teams) && a.teams.length > 0
              ? a.teams
              : (a.team ? [a.team] : [])))
          )].sort();
        } else {
          const membershipTeams = Array.isArray(user?.teams) && user.teams.length > 0
            ? user.teams
            : (user?.team ? [user.team] : []);
          teams = membershipTeams;
        }
        setAvailableTeams(teams);
        
        // Auto-select team for coaches
        if (!isAdmin() && user?.team && !workoutTeam) {
          setWorkoutTeam(user.team);
        }
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchAthletes = async (teamOverride = null) => {
    try {
      const token = localStorage.getItem('token');
      const activeTeam = teamOverride || workoutTeam || (!isAdmin() ? user?.team : null);
      const teamFilter = activeTeam ? `?team=${encodeURIComponent(activeTeam)}` : '';
      const response = await fetch(`${API_URL}/api/athletes${teamFilter}`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setAthleteRoster(data);
      } else {
        console.error('Failed to fetch athletes');
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
    }
  };

  const fetchBoats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/boats`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setBoatRoster(data.boats || []);
      } else {
        console.error('Failed to fetch boats');
      }
    } catch (error) {
      console.error('Error fetching boats:', error);
    }
  };

  const fetchOars = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/oars`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setOarRoster(data.oars || []);
      } else {
        console.error('Failed to fetch oars');
      }
    } catch (error) {
      console.error('Error fetching oars:', error);
    }
  };

  const fetchAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/attendance/future`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        const absenceMap = {};
        data.forEach((record) => {
          if (!absenceMap[record.athlete_name]) {
            absenceMap[record.athlete_name] = [];
          }
          absenceMap[record.athlete_name].push(record.absence_date);
        });
        setAttendanceRecords(absenceMap);
      } else {
        console.error('Failed to fetch attendance');
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  function formatDateMMDDYYYY(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  const getWorkoutTeamQuery = (explicitTeam = null) => {
    if (!isAdmin() && user?.team) {
      return `?team=${encodeURIComponent(user.team)}`;
    }
    if (isAdmin() && explicitTeam) {
      return `?team=${encodeURIComponent(explicitTeam)}`;
    }
    return '';
  };

  const setWorkoutDateFromFormatted = (formattedDate) => {
    const [m, d, y] = formattedDate.split('/');
    setWorkoutDate(new Date(Number(y), Number(m) - 1, Number(d)));
  };

  const fetchPlanForDate = async (formattedDate) => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const response = await fetch(
        `${API_URL}/api/training-plans/for-date?date=${encodeURIComponent(formattedDate)}`,
        { headers: getAuthHeaders(token) }
      );
      if (response.ok) {
        const data = await response.json();
        return data.enabled ? (data.entries || []) : [];
      }
    } catch (error) {
      console.error('Error fetching training plan for date:', error);
    }
    return [];
  };

  const planSessionHasWorkout = (plan, sessions) =>
    sessions.some(
      (s) =>
        s.session_number === plan.sessionNumber &&
        (s.team || '') === (plan.team || '') &&
        (s.squad_id ?? null) === (plan.squadId ?? null)
    );

  const applyPlanEntryToForm = (entry) => {
    setSessionNumber(entry.sessionNumber);
    setWorkoutTitle(entry.title || `${entry.modality} ${entry.plannedMinutes} min`);
    setWorkoutNotes(entry.notes || '');
    setWorkoutGoalPercent(entry.goalPercent != null ? String(entry.goalPercent) : '');
    setBoatings([]);
    setSignupEnabled(false);
    setSignupLimit('');
    const window = getDefaultReservationWindow(entry.sessionNumber);
    setReservationStartTime(window.start);
    setReservationEndTime(window.end);
  };

  const findPlanSession = (plans, sessionNumber, squadId = undefined) =>
    plans.find((p) => {
      if (p.sessionNumber !== sessionNumber) return false;
      if (squadId !== undefined && (p.squadId ?? null) !== (squadId ?? null)) return false;
      return true;
    });

  const loadPlanSession = (formattedDate, plan) => {
    setWorkoutDateFromFormatted(formattedDate);
    setWorkoutTeam(plan.team || user?.team || workoutTeam);
    setWorkoutSquadId(plan.squadId ? String(plan.squadId) : '');
    applyPlanEntryToForm(plan);
    flashStatus(
      `Loaded training plan session ${plan.sessionNumber}${plan.squadName ? ` (${plan.squadName})` : ''} — add boat lineups below, then save.`,
      'success'
    );
  };

  const loadWorkout = async (formattedDate, targetSession = null, targetTeam = null, targetSquadId, options = {}) => {
    const applyStatus = (message, tone = 'success') => {
      if (options.statusMessage) {
        flashStatus(options.statusMessage, options.statusTone || 'success');
        return;
      }
      if (options.silent) return;
      flashStatus(message, tone);
    };

    try {
      const token = localStorage.getItem('token');
      const encodedDate = encodeURIComponent(formattedDate);
      const teamQuery = getWorkoutTeamQuery(targetTeam);
      const [response, planEntries] = await Promise.all([
        fetch(`${API_URL}/api/workouts/${encodedDate}${teamQuery}`, {
          headers: getAuthHeaders(token)
        }),
        fetchPlanForDate(formattedDate)
      ]);

      setPlanSessions(planEntries);
      setWorkoutDateFromFormatted(formattedDate);

      const activeTeam = !isAdmin() && user?.team
        ? user.team
        : (targetTeam || workoutTeam || user?.team || '');

      if (response.ok) {
        const sessions = await response.json();
        setExistingSessions(sessions);

        const sessionToLoad = targetSession
          ? sessions.find((s) => {
              if (s.session_number !== targetSession) return false;
              if (targetSquadId !== undefined && (s.squad_id ?? null) !== (targetSquadId ?? null)) {
                return false;
              }
              if (!activeTeam) return true;
              return (s.team || '') === activeTeam;
            })
          : sessions[0];

        if (sessionToLoad) {
          setWorkoutTitle(sessionToLoad.title);
          setWorkoutNotes(sessionToLoad.notes || '');
          setWorkoutGoalPercent(
            sessionToLoad.goal_percent != null ? String(sessionToLoad.goal_percent) : ''
          );
          setSessionNumber(sessionToLoad.session_number || 1);
          const loadedWindow = getDefaultReservationWindow(sessionToLoad.session_number || 1);
          setReservationStartTime(sessionToLoad.reservation_start_time || loadedWindow.start);
          setReservationEndTime(sessionToLoad.reservation_end_time || loadedWindow.end);
          setWorkoutTeam(sessionToLoad.team || '');
          setWorkoutSquadId(sessionToLoad.squad_id ? String(sessionToLoad.squad_id) : '');
          setBoatings(sessionToLoad.boatings || []);
          setSignupEnabled(sessionToLoad.signup_enabled || false);
          setSignupLimit(sessionToLoad.signup_limit ? sessionToLoad.signup_limit.toString() : '');
          const scopeLabel = [sessionToLoad.team, sessionToLoad.squad_name]
            .filter(Boolean)
            .join(' · ');
          applyStatus(
            `Loaded session ${sessionToLoad.session_number}${scopeLabel ? ` (${scopeLabel})` : ''} for ${formattedDate}.`
          );
          return;
        }

        const teamPlans = planEntries.filter((p) => !activeTeam || p.team === activeTeam);
        const planToLoad = targetSession
          ? findPlanSession(teamPlans, targetSession, targetSquadId)
          : teamPlans.find((p) => !planSessionHasWorkout(p, sessions));

        if (planToLoad) {
          loadPlanSession(formattedDate, planToLoad);
          if (options.statusMessage) applyStatus();
          return;
        }

        applyStatus('No matching session found for that date.', 'error');
        return;
      }

      setExistingSessions([]);

      const teamPlans = planEntries.filter((p) => !activeTeam || p.team === activeTeam);
      const planToLoad = targetSession
        ? findPlanSession(teamPlans, targetSession, targetSquadId)
        : teamPlans[0];

      if (planToLoad) {
        loadPlanSession(formattedDate, planToLoad);
        if (options.statusMessage) applyStatus();
        return;
      }

      if (planEntries.length > 0 && teamPlans.length === 0) {
        applyStatus('Training plan exists for this date, but not for your team.', 'error');
      } else {
        applyStatus('No workout found for that date.', 'error');
      }
    } catch (error) {
      console.error('Error loading workout:', error);
      applyStatus('Error loading workout. Please try again.', 'error');
    }
  };

  const saveWorkout = async () => {
    if (!workoutDate) {
      flashStatus('Please select a workout date.', 'error');
      return;
    }

    if (!workoutTeam) {
      flashStatus('Please select a team for this workout.', 'error');
      return;
    }
    if (!reservationStartTime || !reservationEndTime) {
      flashStatus('Please select the reservation start and end times.', 'error');
      return;
    }
    if (toMinutes(reservationEndTime) <= toMinutes(reservationStartTime)) {
      flashStatus('Reservation end time must be after start time.', 'error');
      return;
    }
    if (toMinutes(reservationStartTime) % 15 !== 0 || toMinutes(reservationEndTime) % 15 !== 0) {
      flashStatus('Reservation times must be in 15-minute increments.', 'error');
      return;
    }

    const formattedDate = formatDateMMDDYYYY(workoutDate);
    const workoutData = {
      title: workoutTitle,
      notes: workoutNotes,
      goalPercent: workoutGoalPercent ? parseFloat(workoutGoalPercent) : null,
      boatings: boatings,
      date: formattedDate,
      sessionNumber: sessionNumber,
      team: workoutTeam,
      squadId: workoutSquadId ? parseInt(workoutSquadId, 10) : null,
      signupEnabled: signupEnabled,
      signupLimit: signupLimit ? parseInt(signupLimit) : null,
      reservationStartTime,
      reservationEndTime,
      attachCrewToReservations
    };

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/workouts`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(workoutData),
      });

      if (response.ok) {
        const data = await response.json();
        const savedSquadName = workoutSquadId
          ? squads.find((s) => String(s.id) === workoutSquadId)?.name
          : null;
        let successMessage = `Session ${sessionNumber} for ${workoutTeam}${savedSquadName ? ` (${savedSquadName})` : ''} saved successfully for ${formattedDate}!`;
        if (data.autoReservationsCreated) {
          successMessage += `\nAuto-reservations created: ${data.autoReservationsCreated}`;
        }
        if (data.reservationWarnings?.length) {
          successMessage += `\n\nReservation warnings:\n- ${data.reservationWarnings.join('\n- ')}`;
        }
        const statusTone = data.reservationWarnings?.length ? 'info' : 'success';
        flashStatus(successMessage, statusTone);
        loadWorkout(formattedDate, sessionNumber, workoutTeam, workoutSquadId ? parseInt(workoutSquadId, 10) : null, {
          statusMessage: successMessage,
          statusTone
        });
      } else {
        const error = await response.json();
        flashStatus(`Failed to save workout: ${error.error || error.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error saving workout:', error);
      flashStatus('Error saving workout. Please try again.', 'error');
    }
  };

  const deleteWorkout = async () => {
    if (!workoutDate) {
      flashStatus('Please select a workout date.', 'error');
      return;
    }

    if (!workoutTeam) {
      flashStatus('Please select a team.', 'error');
      return;
    }

    const formattedDate = formatDateMMDDYYYY(workoutDate);
    const squadName = workoutSquadId
      ? squads.find((s) => String(s.id) === workoutSquadId)?.name
      : null;
    if (
      confirm(
        `Are you sure you want to clear session ${sessionNumber} for ${workoutTeam}${squadName ? ` (${squadName})` : ''} on ${formattedDate}?`
      )
    ) {
      try {
        const token = localStorage.getItem('token');
        const encodedDate = encodeURIComponent(formattedDate);
        const squadQuery = workoutSquadId ? `&squadId=${encodeURIComponent(workoutSquadId)}` : '';
        const response = await fetch(`${API_URL}/api/workouts/${encodedDate}?sessionNumber=${sessionNumber}&team=${encodeURIComponent(workoutTeam)}${squadQuery}`, {
          method: 'DELETE',
          headers: getAuthHeaders(token)
        });

        if (response.ok) {
          setWorkoutTitle('');
          setWorkoutNotes('');
          setWorkoutGoalPercent('');
          setBoatings([]);
          const clearedMessage = `Session ${sessionNumber} for ${workoutTeam} cleared.`;
          flashStatus(clearedMessage, 'success');
          loadWorkout(formattedDate, null, null, undefined, {
            statusMessage: clearedMessage,
            statusTone: 'success'
          });
        } else {
          flashStatus('Failed to delete workout.', 'error');
        }
      } catch (error) {
        console.error('Error deleting workout:', error);
        flashStatus('Error deleting workout. Please try again.', 'error');
      }
    }
  };

  const clearForm = () => {
    setWorkoutTitle('');
    setWorkoutNotes('');
    setWorkoutGoalPercent('');
    setBoatings([]);
    setSessionNumber(1);
    setWorkoutTeam('');
    setWorkoutSquadId('');
    setExistingSessions([]);
    setPlanSessions([]);
    flashStatus('');
  };

  const pendingPlanSessions = planSessions.filter(
    (plan) => !planSessionHasWorkout(plan, existingSessions)
  );

  // Parse and normalize date from various formats to MM/DD/YYYY
  const parseAndNormalizeDate = (dateString) => {
    if (!dateString) return { valid: false, error: 'Missing date' };
    
    const trimmed = dateString.trim();
    
    // Try to match M/D/YY, M/D/YYYY, MM/D/YY, MM/DD/YY, MM/DD/YYYY, etc.
    const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
    const match = trimmed.match(regex);
    
    if (!match) {
      return { valid: false, error: 'Invalid format (use M/D/YY or MM/DD/YYYY)' };
    }
    
    let [, month, day, year] = match;
    month = parseInt(month);
    day = parseInt(day);
    year = parseInt(year);
    
    // Convert 2-digit year to 4-digit (assume 2000s for years <= 50, 1900s for > 50)
    if (year < 100) {
      year = year <= 50 ? 2000 + year : 1900 + year;
    }
    
    // Validate month and day ranges
    if (month < 1 || month > 12) {
      return { valid: false, error: 'Invalid month' };
    }
    if (day < 1 || day > 31) {
      return { valid: false, error: 'Invalid day' };
    }
    
    // Create date and verify it's valid
    const date = new Date(year, month - 1, day);
    if (date.getMonth() + 1 !== month || date.getDate() !== day) {
      return { valid: false, error: 'Invalid date' };
    }
    
    // Return normalized format MM/DD/YYYY
    const normalizedDate = `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
    return { valid: true, normalizedDate };
  };

  const validateDate = (dateString) => {
    return parseAndNormalizeDate(dateString);
  };

  const validateSessionNumber = (session) => {
    const num = parseInt(session);
    if (isNaN(num) || num < 1) {
      return { valid: false, error: 'Session must be 1 or greater' };
    }
    return { valid: true };
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setUploadStatus('');

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const workouts = results.data.map((row, index) => {
          const errors = [];

          // Validate and normalize date
          let normalizedDate = '';
          if (!row.Date || row.Date.trim() === '') {
            errors.push('Missing date');
          } else {
            const dateValidation = parseAndNormalizeDate(row.Date.trim());
            if (!dateValidation.valid) {
              errors.push(`Date: ${dateValidation.error}`);
            } else {
              normalizedDate = dateValidation.normalizedDate;
            }
          }

          // Validate session number (required)
          const sessionNum = row.Session || row.SessionNumber || '1';
          const sessionValidation = validateSessionNumber(sessionNum);
          if (!sessionValidation.valid) {
            errors.push(`Session: ${sessionValidation.error}`);
          }

          if (!row.Title || row.Title.trim() === '') {
            errors.push('Missing title');
          }

          // Validate team (required)
          if (!row.Team || row.Team.trim() === '') {
            errors.push('Missing team');
          }

          return {
            rowNumber: index + 2, // +2 because row 1 is header, index starts at 0
            date: normalizedDate || row.Date?.trim() || '',
            sessionNumber: parseInt(sessionNum) || 1,
            team: row.Team?.trim() || '',
            squad: row.Squad?.trim() || '',
            title: row.Title?.trim() || '',
            notes: row.Notes?.trim() || '',
            errors: errors,
            valid: errors.length === 0
          };
        });

        setCsvPreview(workouts);
        setShowCsvPreview(true);
      },
      error: (error) => {
        alert(`Error parsing CSV: ${error.message}`);
        setCsvFile(null);
      }
    });
  };

  const confirmCsvUpload = async () => {
    const validWorkouts = csvPreview.filter(w => w.valid);

    if (validWorkouts.length === 0) {
      alert('No valid workouts to upload. Please fix errors and try again.');
      return;
    }

    const invalidCount = csvPreview.length - validWorkouts.length;
    const confirmMessage = invalidCount > 0
      ? `Upload ${validWorkouts.length} valid workouts? ${invalidCount} invalid rows will be skipped. This will overwrite existing workouts with matching date, session number, team, and squad.`
      : `Upload ${validWorkouts.length} workouts? This will overwrite existing workouts with matching date, session number, team, and squad.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    setUploadStatus('Uploading...');

    try {
      const token = localStorage.getItem('token');
      const uniqueTeams = [...new Set(validWorkouts.map((w) => w.team).filter(Boolean))];
      const squadMaps = {};
      for (const teamName of uniqueTeams) {
        const res = await fetch(
          `${API_URL}/api/squads?team=${encodeURIComponent(teamName)}`,
          { headers: getAuthHeaders(token) }
        );
        if (res.ok) {
          const data = await res.json();
          squadMaps[teamName] = Object.fromEntries(
            (data.squads || []).map((s) => [s.name.toLowerCase(), s.id])
          );
        } else {
          squadMaps[teamName] = {};
        }
      }

      const workoutsToUpload = [];
      for (const w of validWorkouts) {
        let squadId = null;
        if (w.squad) {
          const squadIdResolved = squadMaps[w.team]?.[w.squad.toLowerCase()];
          if (!squadIdResolved) {
            setUploadStatus(`Upload failed: unknown squad "${w.squad}" for team "${w.team}" (row ${w.rowNumber})`);
            return;
          }
          squadId = squadIdResolved;
        }
        workoutsToUpload.push({
          date: w.date,
          sessionNumber: w.sessionNumber,
          team: w.team,
          squadId,
          title: w.title,
          notes: w.notes,
          boatings: []
        });
      }

      const response = await fetch(`${API_URL}/api/workouts/bulk-upload`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ workouts: workoutsToUpload }),
      });

      if (response.ok) {
        const result = await response.json();
        setUploadStatus(`Success! ${result.created} workouts uploaded.`);
        setShowCsvPreview(false);
        setCsvFile(null);
        setCsvPreview([]);
        setTimeout(() => setUploadStatus(''), 5000);
      } else {
        const error = await response.json();
        setUploadStatus(`Upload failed: ${error.error || error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setUploadStatus('Error uploading workouts. Please try again.');
    }
  };

  const cancelCsvUpload = () => {
    setShowCsvPreview(false);
    setCsvFile(null);
    setCsvPreview([]);
    setUploadStatus('');
  };

  const assignableBoats = getAssignableBoats();
  const statusBannerClass =
    loadStatusTone === 'success'
      ? 'text-green-800 bg-green-50 border-green-200'
      : loadStatusTone === 'error'
        ? 'text-red-800 bg-red-50 border-red-200'
        : 'text-amber-800 bg-amber-50 border-amber-200';
  const renderStatusBanner = () =>
    loadStatus ? (
      <p className={`mt-2 text-xs sm:text-sm border rounded px-3 py-2 whitespace-pre-line ${statusBannerClass}`}>
        {loadStatus}
      </p>
    ) : null;
  const squadAthleteNames = workoutSquadId
    ? new Set(
        squadMembers
          .filter((m) => String(m.squadId) === workoutSquadId)
          .map((m) => m.name)
      )
    : null;
  const splitManageCandidates = oarRoster
    .filter((oar) => !oar.is_retired && !oar.is_virtual_split)
    .sort((a, b) => a.name.localeCompare(b.name));
  const formatOarOptionLabel = (oar) => {
    const typeLabel = OAR_TYPES.find(t => t.value === oar.oar_type)?.label || oar.oar_type;
    const splitBadge = oar.is_virtual_split
      ? ` [SPLIT ${oar.split_label || ''}]`
      : oar.has_active_split_children
        ? ' [PARENT SPLIT]'
        : '';
    return `${oar.name}${splitBadge} (${typeLabel})`;
  };

  return (
    <main className="flex flex-col items-center w-full">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Workout Builder</h1>

      {/* CSV Upload Section */}
      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg w-full max-w-3xl mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Upload Workouts (CSV)</h2>
        <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
          Upload a CSV file with columns: <strong>Date</strong> (M/D/YY or MM/DD/YYYY), <strong>Session</strong> (1+), <strong>Team</strong>, optional <strong>Squad</strong>, <strong>Title</strong>, Notes
        </p>
        <p className="text-xs text-gray-500 mb-3 sm:mb-4 hidden sm:block">
          Session numbers allow multiple workouts per day (e.g., morning = 1, afternoon = 2).
          Squad is optional — leave blank for whole-team workouts.
          Reservation window is optional and can be configured later when lineups are finalized.
          Workouts are only overwritten when date, session number, team, and squad all match.
        </p>

        <input
          type="file"
          accept=".csv"
          onChange={handleCsvFileChange}
          className="border p-2 sm:p-2 rounded mb-2 w-full bg-white text-blue-900 text-sm"
        />

        {uploadStatus && (
          <div className={`mt-2 p-2 rounded ${uploadStatus.startsWith('Success') ? 'bg-green-100 text-green-800' : uploadStatus.startsWith('Error') || uploadStatus.startsWith('Upload failed') ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
            {uploadStatus}
          </div>
        )}

        {showCsvPreview && (
          <div className="mt-4 border rounded p-3 sm:p-4 bg-gray-50">
            <h3 className="font-semibold mb-2 text-sm sm:text-base">Preview ({csvPreview.length} rows)</h3>
            <div className="overflow-x-auto max-h-64 sm:max-h-96 overflow-y-auto">
              {/* Mobile: Card view */}
              <div className="sm:hidden space-y-2">
                {csvPreview.map((workout, idx) => (
                  <div key={idx} className={`p-3 rounded border ${workout.valid ? 'bg-white' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-xs text-gray-500">Row {workout.rowNumber}</span>
                      {workout.valid ? (
                        <span className="text-green-600 text-xs font-semibold">✓ Valid</span>
                      ) : (
                        <span className="text-red-600 text-xs">{workout.errors.join(', ')}</span>
                      )}
                    </div>
                    <p className="font-semibold text-sm">{workout.title}</p>
                    <p className="text-xs text-gray-600">
                      {workout.date} • Session {workout.sessionNumber} • {workout.team}
                      {workout.squad ? ` • ${workout.squad}` : ''}
                    </p>
                  </div>
                ))}
              </div>
              {/* Desktop: Table view */}
              <table className="w-full text-sm border-collapse hidden sm:table">
                <thead className="bg-gray-200 sticky top-0">
                  <tr>
                    <th className="border p-2 text-left">Row</th>
                    <th className="border p-2 text-left">Date</th>
                    <th className="border p-2 text-left">Session</th>
                    <th className="border p-2 text-left">Team</th>
                    <th className="border p-2 text-left">Squad</th>
                    <th className="border p-2 text-left">Title</th>
                    <th className="border p-2 text-left">Notes</th>
                    <th className="border p-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.map((workout, idx) => (
                    <tr key={idx} className={workout.valid ? '' : 'bg-red-50'}>
                      <td className="border p-2">{workout.rowNumber}</td>
                      <td className="border p-2">{workout.date}</td>
                      <td className="border p-2">{workout.sessionNumber}</td>
                      <td className="border p-2">{workout.team}</td>
                      <td className="border p-2">{workout.squad || '—'}</td>
                      <td className="border p-2">{workout.title}</td>
                      <td className="border p-2 max-w-xs truncate">{workout.notes}</td>
                      <td className="border p-2">
                        {workout.valid ? (
                          <span className="text-green-600 font-semibold">Valid</span>
                        ) : (
                          <span className="text-red-600 text-xs">
                            {workout.errors.join(', ')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                onClick={confirmCsvUpload}
                className="btn-theme-primary px-4 py-3 sm:py-2 rounded text-sm sm:text-base"
              >
                Confirm Upload ({csvPreview.filter(w => w.valid).length} valid)
              </button>
              <button
                onClick={cancelCsvUpload}
                className="bg-gray-500 text-white px-4 py-3 sm:py-2 rounded hover:bg-gray-600 text-sm sm:text-base"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg w-full max-w-3xl">
          <h2 className="text-xl sm:text-2xl font-bold mb-4">Daily Workout Details</h2>
          <div className="mb-4 sm:mb-6">
            <label className="block text-sm font-semibold mb-1">
              Load Existing Workout:
            </label>
            <input
              type="date"
              onChange={(e) => {
                const selectedDate = e.target.value;
                const [year, month, day] = selectedDate.split('-');
                const formattedDate = `${month}/${day}/${year}`;
                loadWorkout(formattedDate);
              }}
              className="border p-3 sm:p-2 rounded mb-2 bg-white text-blue-900 text-base w-full sm:w-auto"
            />
            
            {renderStatusBanner()}

            {/* Show existing sessions for selected date */}
            {existingSessions.length > 0 && (
              <div className="mt-2 p-2 sm:p-3 bg-gray-50 rounded border">
                <p className="text-xs sm:text-sm font-semibold mb-2">Sessions for this date:</p>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                  {existingSessions.map((session) => (
                    <button
                      key={`${session.session_number}-${session.team || 'none'}-${session.squad_id || 'team'}`}
                      onClick={() => loadWorkout(session.date, session.session_number, session.team, session.squad_id ?? null)}
                      className={`px-3 py-2 sm:py-1 rounded text-xs sm:text-sm text-left ${
                        sessionNumber === session.session_number &&
                        workoutTeam === (session.team || '') &&
                        (workoutSquadId || '') === (session.squad_id ? String(session.squad_id) : '')
                          ? 'btn-theme-primary'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {isAdmin() && session.team && (
                        <span className="font-semibold">[{session.team}] </span>
                      )}
                      {session.squad_name && (
                        <span className="font-semibold">[{session.squad_name}] </span>
                      )}
                      Session {session.session_number}: {session.title}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      const teamSessions = existingSessions.filter(
                        (s) => !workoutTeam || (s.team || '') === workoutTeam
                      );
                      const nextSession = teamSessions.length > 0
                        ? Math.max(...teamSessions.map((s) => s.session_number)) + 1
                        : 1;
                      const nextWindow = getDefaultReservationWindow(nextSession);
                      setSessionNumber(nextSession);
                      setReservationStartTime(nextWindow.start);
                      setReservationEndTime(nextWindow.end);
                      setWorkoutTitle('');
                      setWorkoutNotes('');
                      setWorkoutGoalPercent('');
                      setBoatings([]);
                      setWorkoutSquadId('');
                    }}
                    className="px-3 py-2 sm:py-1 rounded text-xs sm:text-sm bg-green-100 text-green-700 hover:bg-green-200"
                  >
                    + Add New Session
                  </button>
                </div>
              </div>
            )}

            {pendingPlanSessions.length > 0 && workoutDate && (
              <div className="mt-2 p-2 sm:p-3 bg-amber-50 rounded border border-amber-200">
                <p className="text-xs sm:text-sm font-semibold mb-2 text-amber-900">
                  Training plan — add lineups:
                </p>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                  {pendingPlanSessions.map((plan) => (
                    <button
                      key={`plan-${plan.sessionNumber}-${plan.team || 'none'}-${plan.squadId || 'team'}`}
                      type="button"
                      onClick={() => loadPlanSession(formatDateMMDDYYYY(workoutDate), plan)}
                      className={`px-3 py-2 sm:py-1 rounded text-xs sm:text-sm text-left ${
                        sessionNumber === plan.sessionNumber &&
                        workoutTeam === (plan.team || '') &&
                        (workoutSquadId || '') === (plan.squadId ? String(plan.squadId) : '')
                          ? 'bg-amber-600 text-white'
                          : 'bg-amber-100 text-amber-900 hover:bg-amber-200'
                      }`}
                    >
                      {isAdmin() && plan.team && (
                        <span className="font-semibold">[{plan.team}] </span>
                      )}
                      {plan.squadName && (
                        <span className="font-semibold">[{plan.squadName}] </span>
                      )}
                      Session {plan.sessionNumber}:{' '}
                      {plan.title || `${plan.modality} ${plan.plannedMinutes} min`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Team and Session Selection - Side by side on larger screens */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold mb-1">
                Team: <span className="text-red-500">*</span>
              </label>
              <select
                value={workoutTeam}
                onChange={(e) => setWorkoutTeam(e.target.value)}
                disabled={!isAdmin()}
                className="border p-3 sm:p-2 rounded w-full bg-white text-blue-900 text-base disabled:bg-gray-100"
                required
              >
                <option value="">Select a Team</option>
                {availableTeams.map((team) => (
                  <option key={team} value={team}>
                    {team}
                  </option>
                ))}
              </select>
              {availableTeams.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No teams found. Add athletes with team assignments first.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                Squad:
              </label>
              <div className="flex gap-2">
                <select
                  value={workoutSquadId}
                  onChange={(e) => setWorkoutSquadId(e.target.value)}
                  disabled={!workoutTeam}
                  className="border p-3 sm:p-2 rounded flex-1 bg-white text-blue-900 text-base disabled:bg-gray-100"
                >
                  <option value="">Whole team</option>
                  {squads.map((squad) => (
                    <option key={squad.id} value={String(squad.id)}>
                      {squad.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowSquadManager(true)}
                  disabled={!workoutTeam}
                  className="bg-gray-200 text-gray-700 px-3 py-2 rounded hover:bg-gray-300 text-sm whitespace-nowrap disabled:opacity-50"
                >
                  Manage squads
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Tag this session to one squad, or leave as whole team.
                {workoutSquadId && squadAthleteNames && (
                  <span> Lineup picker shows {squadAthleteNames.size} squad athletes.</span>
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                Session Number:
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={sessionNumber}
                onChange={(e) => {
                  const nextSession = Math.max(1, parseInt(e.target.value, 10) || 1);
                  const nextWindow = getDefaultReservationWindow(nextSession);
                  setSessionNumber(nextSession);
                  setReservationStartTime(nextWindow.start);
                  setReservationEndTime(nextWindow.end);
                }}
                className="border p-3 sm:p-2 rounded w-full sm:w-32 bg-white text-blue-900 text-base"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold mb-1">
                Reservation Start Time:
              </label>
              <input
                type="time"
                step={900}
                value={reservationStartTime}
                onChange={(e) => setReservationStartTime(e.target.value)}
                className="border p-3 sm:p-2 rounded w-full bg-white text-blue-900 text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">
                Reservation End Time:
              </label>
              <input
                type="time"
                step={900}
                value={reservationEndTime}
                onChange={(e) => setReservationEndTime(e.target.value)}
                className="border p-3 sm:p-2 rounded w-full bg-white text-blue-900 text-base"
              />
            </div>
          </div>

          <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="attachCrewToReservations"
                checked={attachCrewToReservations}
                onChange={(e) => setAttachCrewToReservations(e.target.checked)}
                className="w-5 h-5 mt-0.5 text-theme-primary border-gray-300 rounded focus:ring-theme-primary cursor-pointer"
              />
              <div>
                <label htmlFor="attachCrewToReservations" className="text-sm font-semibold cursor-pointer block">
                  Add crew to auto-reservations
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  When this session is saved, boat time slots are reserved automatically. Check this to copy names from
                  the seat chart (where they match club athlete profiles) onto each reservation, up to that boat’s seat
                  count, so logging meters can credit the crew. Uncheck to reserve boats without a crew list. To hold
                  boats without posting a workout or lineups, use Equipment → Mass Reserve.
                </p>
              </div>
            </div>
          </div>

          <input
            type="text"
            placeholder="Workout Title"
            value={workoutTitle}
            onChange={(e) => setWorkoutTitle(e.target.value)}
            className="border p-3 sm:p-2 rounded mb-4 w-full text-blue-900 bg-white text-base"
          />
          <textarea
            placeholder="Workout Notes"
            value={workoutNotes}
            onChange={(e) => setWorkoutNotes(e.target.value)}
            className="border p-3 sm:p-2 rounded mb-4 w-full text-blue-900 bg-white text-base min-h-[100px]"
            rows={3}
          />
          {targetTimesEnabled && (
          <div className="mb-4">
            <GoalPercentPicker
              value={workoutGoalPercent}
              onChange={setWorkoutGoalPercent}
              hint="Pick a stroke rate to fill the chart percent, then keep it or type a different % of 2k. Target splits use the percent."
            />
          </div>
          )}

          {/* Sign-Up Settings */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-3 mb-3">
              <input
                type="checkbox"
                id="signupEnabled"
                checked={signupEnabled}
                onChange={(e) => setSignupEnabled(e.target.checked)}
                className="w-5 h-5 text-theme-primary border-gray-300 rounded focus:ring-theme-primary cursor-pointer"
              />
              <label htmlFor="signupEnabled" className="text-sm font-semibold cursor-pointer">
                Enable Athlete Sign-Ups
              </label>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              When enabled, athletes can sign themselves up for this workout instead of being assigned to boats.
            </p>
            
            {signupEnabled && (
              <div className="flex items-center gap-3 mt-3 pt-3 border-t">
                <label htmlFor="signupLimit" className="text-sm font-medium">
                  Signup Limit (optional):
                </label>
                <input
                  type="number"
                  id="signupLimit"
                  value={signupLimit}
                  onChange={(e) => setSignupLimit(e.target.value)}
                  placeholder="Unlimited"
                  min="1"
                  className="border p-2 rounded w-24 text-blue-900 bg-white text-sm"
                />
                <span className="text-xs text-gray-500">Leave blank for unlimited</span>
              </div>
            )}
          </div>
          
          {/* Date Selection - Native input on mobile, calendar on desktop */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-1">
              Workout Date:
            </label>
            {/* Mobile: Native date input */}
            <input
              type="date"
              value={workoutDate ? getLocalISODate(workoutDate) : ''}
              onChange={(e) => {
                const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                setWorkoutDate(date);
                if (date) {
                  loadWorkout(formatDateMMDDYYYY(date));
                }
              }}
              min={getLocalISODate()}
              className="border p-3 sm:p-2 rounded w-full bg-white text-blue-900 text-base sm:hidden"
            />
            {/* Desktop: Calendar picker */}
            <div className="hidden sm:block">
            <DayPicker
              mode="single"
              selected={workoutDate}
                onSelect={(date) => {
                  setWorkoutDate(date);
                  if (date) {
                    loadWorkout(formatDateMMDDYYYY(date));
                  }
                }}
              disabled={{ before: new Date() }}
              className="rounded-lg border p-4 bg-white"
              showOutsideDays
            />
            </div>
          </div>
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              <h3 className="text-lg sm:text-xl font-semibold">Boatings</h3>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setShowOarSplitManageModal(true)}
                  disabled={splitManageCandidates.length === 0}
                  className="px-3 py-2 rounded border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm w-full sm:w-auto"
                >
                  Manage Oar Splits
                </button>
                <button
                  type="button"
                  onClick={openOarAssignModal}
                  disabled={assignableBoats.length === 0}
                  className="px-3 py-2 rounded border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm w-full sm:w-auto"
                >
                  Assign Oar Sets
                </button>
              </div>
            </div>
            {boatings.map((boat, boatIndex) => {
              // Migrate old format if needed
              const migratedBoat = migrateBoatingData(boat);
              if (migratedBoat !== boat) {
                // Update state if migration was needed
                const updated = [...boatings];
                updated[boatIndex] = migratedBoat;
                setBoatings(updated);
              }
              
              const seats = migratedBoat.seats || [];
              
              return (
                <div key={boatIndex} className="relative border p-3 sm:p-4 mb-4 rounded bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs sm:text-sm font-medium text-gray-500">Boat {boatIndex + 1}</span>
                    <button
                      onClick={() => removeBoat(boatIndex)}
                      className="text-red-500 hover:text-red-700 text-sm p-1"
                      title="Remove this boat"
                    >
                      ✕
                    </button>
                  </div>
                  <select
                    value={boat.boatName}
                    onChange={(e) => updateBoatSelection(boatIndex, e.target.value)}
                    className="border p-3 sm:p-2 rounded mb-3 w-full text-blue-900 bg-white text-base"
                  >
                    <option value="">Select a Boat</option>
                    {boatRoster
                      .filter(boatOption => {
                        if (!isRowingShell(boatOption.boat_class)) return false;
                        // Filter out boats already assigned to other lineups in this workout
                        const assignedBoatNames = boatings
                          .filter((b, i) => i !== boatIndex && b.boatName)
                          .map(b => b.boatName);
                        return !assignedBoatNames.includes(boatOption.name);
                      })
                      .map((boatOption) => (
                        <option key={boatOption.id} value={boatOption.name}>
                          {boatOption.name} ({boatOption.boat_class}{boatOption.weight_class ? ` - ${boatOption.weight_class}` : ''})
                        </option>
                      ))}
                  </select>
                  
                  {/* Oar Selection (optional) - only show for shells, not ergs */}
                  {isRowingShell(boat.boatClass) && (
                  <div className="mb-3">
                    <label className="block text-xs text-gray-500 mb-1">Oars (optional)</label>
                    <select
                      value={boat.oarId || ''}
                      onChange={(e) => {
                        const selectedOar = oarRoster.find(o => o.id === parseInt(e.target.value));
                        updateOarSelection(boatIndex, e.target.value ? parseInt(e.target.value) : null, selectedOar?.name || '');
                      }}
                      className="border p-2 rounded w-full text-blue-900 bg-white text-sm"
                    >
                      <option value="">No oars assigned</option>
                      {oarRoster
                        .filter(oar => {
                          // Filter out oars already assigned to other boats
                          const assignedOarIds = boatings
                            .filter((b, i) => i !== boatIndex && b.oarId)
                            .map(b => b.oarId);
                          return !assignedOarIds.includes(oar.id);
                        })
                        .filter(oar => !oar.is_retired)
                        .filter(oar => !oar.has_active_split_children)
                        .map((oar) => (
                          <option key={oar.id} value={oar.id}>
                            {formatOarOptionLabel(oar)}
                          </option>
                        ))}
                    </select>
                  </div>
                  )}
                  
                  {/* Seat Assignments */}
                  {seats.length > 0 && isRowingShell(boat.boatClass) && (
                    <div className="mb-3 p-2 sm:p-3 bg-white border rounded">
                      <label className="block text-xs text-gray-500 mb-1">Apply saved lineup</label>
                      <select
                        value={lineupApplySelect[boatIndex] || ''}
                        onChange={(e) => {
                          const id = e.target.value;
                          setLineupApplySelect((prev) => ({ ...prev, [boatIndex]: id }));
                          if (id) applySavedLineup(boatIndex, id);
                        }}
                        className="border p-2 rounded w-full text-blue-900 bg-white text-sm"
                      >
                        <option value="">Fill seats one by one, or choose a lineup…</option>
                        {savedLineups
                          .filter((lineup) => lineup.boatClass === boat.boatClass)
                          .map((lineup) => (
                            <option key={lineup.id} value={lineup.id}>
                              {lineup.name} ({(lineup.seats || []).filter((s) => s.athlete).length}/{(lineup.seats || []).length} seated)
                            </option>
                          ))}
                      </select>
                      {savedLineups.filter((lineup) => lineup.boatClass === boat.boatClass).length === 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                          No {boat.boatClass} lineups yet. Save this boat or create one in the Lineups tab.
                        </p>
                      )}
                    </div>
                  )}

                  {seats.length > 0 && (
                    <>
                      <h4 className="font-semibold mt-2 mb-2 text-sm sm:text-base">
                        Seat Assignments ({seats.length} {seats.some(s => s.seat === 'Cox') ? `seats (${seats.length - 1} rowers + cox)` : 'seats'})
                      </h4>
                      <div className="space-y-2">
                        {seats.some((s) => s.seat === 'Cox') && (
                          <p className="text-xs text-gray-500 mb-2">Cox can only be filled by someone with the Coxswain role.</p>
                        )}
                        {seats.map((seatData, seatIndex) => {
                          const isCox = seatData.seat === 'Cox';
                          
                          // Get available athletes for this seat
                          const availableAthletes = athleteRoster
                            .filter((a) => {
                              if (squadAthleteNames && !squadAthleteNames.has(a.name)) {
                                return false;
                              }
                              if (isCox && a.user_role !== 'coxswain') {
                                return false;
                              }
                              // Filter out injured athletes
                              return !a.is_injured;
                            })
                            .map((a) => a.name)
                            .filter((name) => {
                              // Filter out absent athletes
                              const absentDates = attendanceRecords[name] || [];
                              const formattedDate = workoutDate ? formatDateMMDDYYYY(workoutDate) : '';
                              return !absentDates.includes(formattedDate);
                            })
                            .filter((name) => {
                              // Filter out athletes in other boats
                              const allSelectedInOtherBoats = boatings.flatMap((b, i) =>
                                i === boatIndex ? [] : (b.seats || b.athletes || []).map(s => typeof s === 'object' ? s.athlete : s).filter(a => a)
                              );
                              return !allSelectedInOtherBoats.includes(name);
                            })
                            .filter((name) => {
                              // Allow current selection, filter duplicates in same boat
                              const boatAthletes = seats.map(s => s.athlete);
                              const currentSelection = seatData.athlete;
                              const count = boatAthletes.filter((n) => n === name).length;
                              return name === currentSelection || count === 0;
                            });

                          return (
                            <div key={seatIndex} className="flex items-center gap-2">
                              {/* Seat Label */}
                              <div className={`w-16 sm:w-20 flex-shrink-0 text-center py-2 px-2 rounded text-xs sm:text-sm font-semibold ${
                                isCox 
                                  ? 'bg-purple-100 text-purple-700 border border-purple-300' 
                                  : seatData.seat === 'Stroke'
                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                    : seatData.seat === 'Bow'
                                      ? 'bg-green-100 text-green-700 border border-green-300'
                                      : 'bg-gray-100 text-gray-700 border border-gray-300'
                              }`}>
                                {seatData.seat}
                              </div>
                              
                              {/* Athlete Selection */}
                              <div className="flex-1 min-w-0">
                                <AthleteSearchInput
                                  value={seatData.athlete}
                                  onChange={(name) => updateSeatAthlete(boatIndex, seatIndex, name)}
                                  availableAthletes={availableAthletes}
                                  placeholder={isCox ? "Select coxswain..." : "Select athlete..."}
                                />
                              </div>
                              
                              {/* Clear button */}
                              {seatData.athlete && (
                                <button
                                  type="button"
                                  onClick={() => updateSeatAthlete(boatIndex, seatIndex, '')}
                                  className="text-gray-400 hover:text-red-500 text-lg p-1"
                                  title="Clear seat"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {isRowingShell(boat.boatClass) && (
                        <div className="mt-3 flex flex-col sm:flex-row sm:flex-wrap gap-2">
                          {savingLineupBoatIndex === boatIndex ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <input
                                type="text"
                                value={saveLineupName}
                                onChange={(e) => setSaveLineupName(e.target.value)}
                                placeholder="Lineup name (e.g. 1v)"
                                className="border p-2 rounded bg-white text-gray-900 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => saveBoatAsLineup(boatIndex)}
                                className="btn-theme-primary px-3 py-2 rounded text-sm"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setSavingLineupBoatIndex(null);
                                  setSaveLineupName('');
                                }}
                                className="bg-gray-200 text-gray-700 px-3 py-2 rounded text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setSavingLineupBoatIndex(boatIndex);
                                setSaveLineupName('');
                              }}
                              className="text-sm px-3 py-2 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                            >
                              Save as lineup
                            </button>
                          )}
                          <select
                            value={lineupUpdateSelect[boatIndex] || ''}
                            onChange={(e) => {
                              const id = e.target.value;
                              setLineupUpdateSelect((prev) => ({ ...prev, [boatIndex]: id }));
                              if (id) updateExistingLineupFromBoat(boatIndex, id);
                            }}
                            className="border p-2 rounded bg-white text-gray-900 text-sm"
                          >
                            <option value="">Update existing lineup…</option>
                            {savedLineups
                              .filter((lineup) => lineup.boatClass === boat.boatClass)
                              .map((lineup) => (
                                <option key={lineup.id} value={lineup.id}>
                                  Update “{lineup.name}”
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}
                  
                  {!boat.boatName && (
                    <p className="text-sm text-gray-500 italic mt-2">
                      Select a boat to see available seats
                    </p>
                  )}

                  {/* Display Options */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <p className="text-xs sm:text-sm font-semibold text-gray-600 mb-2">Display Options:</p>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={boat.showAvgWeight || false}
                          onChange={(e) => updateBoatOption(boatIndex, 'showAvgWeight', e.target.checked)}
                          className="w-4 h-4 text-theme-primary rounded"
                        />
                        <span className="text-sm">Show Avg Weight</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={boat.showAvgErg || false}
                          onChange={(e) => updateBoatOption(boatIndex, 'showAvgErg', e.target.checked)}
                          className="w-4 h-4 text-theme-primary rounded"
                        />
                        <span className="text-sm">Show Avg 2k Erg</span>
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
            <button
              onClick={addBoat}
              className="btn-theme-primary px-4 py-3 sm:py-2 rounded mt-2 w-full sm:w-auto text-sm sm:text-base"
            >
              + Add Boat
            </button>
          </div>

          {showOarAssignModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-4 sm:p-6">
                <h3 className="text-lg font-bold mb-2">Assign Oar Sets to Boats</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Uniqueness is enforced: each oar set can only be assigned to one boat in this session.
                </p>

                {assignableBoats.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No shell boats are currently available for oar assignment.</p>
                ) : (
                  <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                    {assignableBoats.map(({ boat, index }) => {
                      const selectedElsewhere = Object.entries(modalOarAssignments)
                        .filter(([boatIndex, oarId]) => Number(boatIndex) !== index && oarId)
                        .map(([, oarId]) => parseInt(oarId, 10));

                      return (
                        <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-2 items-center border rounded p-3">
                          <div className="text-sm">
                            <p className="font-semibold">{boat.boatName}</p>
                            <p className="text-gray-500">{boat.boatClass}</p>
                          </div>
                          <select
                            value={modalOarAssignments[index] || ''}
                            onChange={(e) => setModalOarAssignments(prev => ({ ...prev, [index]: e.target.value }))}
                            className="border p-2 rounded w-full text-sm"
                          >
                            <option value="">No oars assigned</option>
                            {oarRoster
                              .filter(oar => !oar.is_retired)
                              .filter(oar => !oar.has_active_split_children)
                              .filter(oar => !selectedElsewhere.includes(oar.id) || String(oar.id) === (modalOarAssignments[index] || ''))
                              .map(oar => (
                                <option key={oar.id} value={oar.id}>
                                  {formatOarOptionLabel(oar)}
                                </option>
                              ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2 mt-5">
                  <button
                    type="button"
                    onClick={applyModalOarAssignments}
                    className="btn-theme-primary px-4 py-2 rounded w-full sm:w-auto"
                  >
                    Apply Assignments
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowOarAssignModal(false)}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {showOarSplitManageModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-4 sm:p-6">
                <h3 className="text-lg font-bold mb-2">Manage Oar Splits</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Split creates N assignable virtual sets (A/B/C...). Recombine retires active split children and restores the parent.
                </p>

                {splitManageCandidates.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No eligible oar sets available.</p>
                ) : (
                  <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                    {splitManageCandidates.map((oar) => {
                      const hasChildren = !!oar.has_active_split_children;
                      const isBusy = oarSplitActionLoadingId === oar.id;
                      return (
                        <div key={oar.id} className="border rounded p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="text-sm">
                            <p className="font-semibold">{oar.name}</p>
                            <p className="text-gray-500">
                              {hasChildren ? 'Status: Split active' : 'Status: Not split'}
                            </p>
                          </div>
                          {hasChildren ? (
                            <button
                              type="button"
                              onClick={() => handleRecombineOarSet(oar)}
                              disabled={isBusy}
                              className="px-3 py-2 rounded border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 text-sm"
                            >
                              {isBusy ? 'Recombining...' : 'Recombine'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleSplitOarSet(oar)}
                              disabled={isBusy}
                              className="px-3 py-2 rounded border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50 text-sm"
                            >
                              {isBusy ? 'Splitting...' : 'Split'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-end mt-5">
                  <button
                    type="button"
                    onClick={() => setShowOarSplitManageModal(false)}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 w-full sm:w-auto"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6">
          <button
            onClick={saveWorkout}
              className="btn-theme-primary px-6 py-3 rounded-lg text-base order-1 sm:order-1"
          >
              Save Session {sessionNumber}
          </button>
          <button
            onClick={deleteWorkout}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700"
          >
              Clear Session {sessionNumber}
            </button>
            <button
              onClick={clearForm}
              className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600"
            >
              Clear Form
          </button>
          </div>
          {renderStatusBanner()}
        </div>

      {showSquadManager && (
        <SquadManager
          team={workoutTeam}
          onClose={() => setShowSquadManager(false)}
          onChanged={() => {
            fetchSquadsForTeam(workoutTeam);
            fetchAthletes(workoutTeam);
          }}
        />
      )}
    </main>
  );
}
